import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ConceptBuildingData from "./ConceptBuildingData";
import { getStudentTasks, TasksChip } from "./ModuleTasks/StudentTasks";
import WrapUpSessionData from "./WrapUpSessionData";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useDispatch } from "react-redux";
import { setSelectedModuleId } from "../new-homepage/redux/moduleSlice";
import ErrorToast from "components/Toaster/ErrorToast";
import Toaster from "components/Toaster/Toaster";
import { useSelector } from "react-redux";
import api from "../api";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AdhocScheduleMeetingModal from './AdhocScheduleMeetingModal';

const ModulesCard = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [courses, setCourses] = useState([]);
  const [openAdhocMeeting, setOpenAdhocMeeting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterSeverity, setToasterSeverity] = useState("success");
  const { id } = useParams();
  const [availableProblems, setAvailableProblems] = useState({});
  const [assessmentStats, setAssessmentStats] = useState({}); // Batch stats for all topics
  const [moduleTasks, setModuleTasks] = useState({}); // A student's assessments and assignments per module
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.userProfile);
  const userRole = userProfile?.data?.role;

  const handleToasterClose = () => {
    setToasterOpen(false);
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const fetchCourseData = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        throw new Error("No access token found in local storage");
      }

      if (!id) {
        throw new Error("No course ID found in URL parameters");
      }

      const response = await api.get(`course/${id}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response?.data) {
        setCourses(response?.data);
      } else {
        <ErrorToast message="No data found." />;
      }
    } catch (err) {
      <ErrorToast message={`Failed to fetch course data: ${err}`} />;
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableProblems = async (moduleId) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await api.get(
        `course/available-problem-types/${moduleId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setAvailableProblems((prev) => ({
        ...prev,
        [moduleId]: response.data,
      }));
    } catch (err) {
      <ErrorToast
        message={`Failed to fetch available problems for module ${moduleId}: ${err}`}
      />;
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  // Fetch batch assessment stats for mentor (single API call for all modules)
  useEffect(() => {
    const fetchBatchStats = async () => {
      if (userRole !== "mentor" || !id) return;
      try {
        const response = await api.get(`course/${id}/mentor/assessment-stats-batch/`);
        setAssessmentStats(response.data?.stats || {});
      } catch (err) {
        // Stats are optional, silently fail
      }
    };
    fetchBatchStats();
  }, [userRole, id]);

  // A student's assessments and assignments for every module (single API call).
  useEffect(() => {
    if (userRole === "mentor" || !id) return;
    let cancelled = false;
    getStudentTasks(id)
      .then((modules) => { if (!cancelled) setModuleTasks(modules || {}); })
      .catch(() => {});            // the cards still work without it
    return () => { cancelled = true; };
  }, [userRole, id]);

  const toggleAccordion = (course) => {
    if(!course.is_active && userRole == "mentee"){
      return
    }
    if (expandedId === course.id) {
      setExpandedId(null);
    } else {
      setExpandedId(course.id);
      dispatch(setSelectedModuleId(course.id));
      if (!availableProblems[course.id]) {
        fetchAvailableProblems(course.id);
      }
    }
  };

  const handleToggleComplete = async (event, course) => {
    event.stopPropagation();
    setLoading(true);
  
    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        throw new Error("No access token found in local storage");
      }
  
      if (!course || !course.id) {
        throw new Error("Invalid course data");
      }
  
      await api.patch(
        `course/${course.id}/customize-course-topic/`,
        { is_completed: !course.is_completed },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      setToasterMessage(
        course.is_completed
          ? `Module "${course.name}" marked as uncompleted.`
          : `Module "${course.name}" marked as completed.`
      );
      setToasterSeverity("success");
      setToasterOpen(true);
  
      fetchCourseData();
    } catch (err) {
      setToasterMessage(`Failed to update module completion status.`);
      setToasterSeverity("error");
      setToasterOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (event, course) => {
    event.stopPropagation();
    setLoading(true);

    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        throw new Error("No access token found in local storage");
      }

      if (!course || !course.id) {
        throw new Error("Invalid course data");
      }

      await api.patch(
        `course/${course.id}/customize-course-topic/`,
        { is_active: !course.is_active },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setToasterMessage(
        course.is_active
          ? `Module "${course.name}" marked as inactive.`
          : `Module "${course.name}" marked as active.`
      );
      setToasterSeverity("success");
      setToasterOpen(true);

      fetchCourseData();
    } catch (err) {
      setToasterMessage(`Failed to update module active status.`);
      setToasterSeverity("error");
      setToasterOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "25px",
          color: "#202124",
          fontFamily: "Be Vietnam Pro",
          marginBottom: "16px"
        }}
      >
        Course Modules
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <CircularProgress />
        </Box>
      ) : courses && courses?.length > 0 ? (
        courses?.map((course, index) => (
          <Grid item xs={12} mb={3} key={course?.id}>
            <Accordion
              sx={{
                "& .MuiAccordionSummary-expandIcon": { display: "none" },
                padding: "8px",
                borderRadius: "10px",
              }}
              p={1}
              expanded={expandedId === course.id}
              onChange={() => toggleAccordion(course)}
            >
              <AccordionSummary>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography
                      sx={{
                        color: "#868686",
                        fontWeight: 400,
                        fontSize: "14px",
                        fontFamily: "Be Vietnam Pro",
                      }}
                      gutterBottom
                    >
                      MODULE {index + 1}
                    </Typography>
                    {userRole === "mentor" ? (
                      <Box display={"flex"} gap={"4px"}>
                        <Tooltip title={course.is_active ? "Mark Inactive" : "Mark Active"}>
                          <PlayCircleOutlineIcon
                            sx={{
                              color: course.is_active ? "#22c55e" : "#6b7280",
                              width: "20px",
                              height: "20px",
                              fontFamily: "Be Vietnam Pro",
                            }}
                            onClick={(event) => handleToggleActive(event, course)}
                          />
                        </Tooltip>
                        <Tooltip
                          title={
                            course.is_completed
                              ? "Mark as uncompleted"
                              : "Mark as completed"
                          }
                        >
                          {course.is_completed ? (
                            <CheckCircleOutlineIcon
                              sx={{
                                color: "green",
                                width: "20px",
                                height: "20px",
                                fontFamily: "Be Vietnam Pro",
                              }}
                              onClick={(event) => handleToggleComplete(event, course)}
                            />
                          ) : (
                            <RadioButtonUncheckedIcon
                              sx={{
                                color: "#6b7280",
                                width: "20px",
                                height: "20px",
                                fontFamily: "Be Vietnam Pro",
                              }}
                              onClick={(event) => handleToggleComplete(event, course)}
                            />
                          )}
                        </Tooltip>
                      </Box>
                    ) : (
                      ""
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "18px",
                      color: "#202124",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {course.name}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    mt={2}
                  >
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={
                        course.is_completed
                          ? "Completed"
                          : course.is_active
                          ? "● In Progress"
                          : "Not Assigned"
                      }
                      sx={{
                        fontSize: "14px",
                        borderRadius: "8px",
                        fontFamily: "Be Vietnam Pro",
                        backgroundColor: course.is_completed
                          ? "#E8F5E9"
                          : course.is_active
                          ? "#E3F2FD"
                          : "#EEEEEE",
                        color: course.is_completed
                          ? "#22c55e"
                          : course.is_active
                          ? "#1e40af"
                          : "#616161",
                        '& .MuiChip-label': {
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        },
                        height: '28px',
                      }}
                    />
                    {userRole !== "mentor" && <TasksChip tasks={moduleTasks[course.id]} />}
                    </Box>
                    <Box display={"flex"} gap={2} alignItems="center">
                      <Button
                        onClick={() => {
                          if (course.is_active) {
                            toggleAccordion(course);
                          }
                        }}
                        variant="outlined"
                        sx={{
                          backgroundColor: "#DDF4FF",
                          textTransform: "capitalize",
                          height: "28px",
                          fontFamily: "Be Vietnam Pro",
                        }}
                        size="small"
                        disabled={!course.is_active && userRole == "mentee"}
                      >
                        {expandedId === course.id
                          ? "Hide Details"
                          : "View Details"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ marginTop: "8px" }}>
                {course.is_last && !course.is_cohort ? (
                  <>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={selectedTab}
                        onChange={handleChangeTab}
                        variant="fullWidth"
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        <Tab
                          label="A. Concept Building"
                          sx={{ fontFamily: "Be Vietnam Pro" }}
                        />
                        <Tab
                          label="B. Wrap-up Session"
                          sx={{ fontFamily: "Be Vietnam Pro" }}
                        />
                      </Tabs>
                    </Box>
                    <Box>
                      {selectedTab === 0 && (
                        <ConceptBuildingData
                          course={course}
                          problems={availableProblems[course.id]}
                          fetchCourseData={fetchCourseData}
                          assessmentStats={assessmentStats[course.id]}
                          tasks={moduleTasks[course.id]}
                        />
                      )}
                      {selectedTab === 1 && (
                        <WrapUpSessionData
                          course={course}
                          problems={availableProblems[course.id]}
                          fetchCourseData={fetchCourseData}
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <ConceptBuildingData
                    course={course}
                    problems={availableProblems[course.id]}
                    fetchCourseData={fetchCourseData}
                    assessmentStats={assessmentStats[course.id]}
                    tasks={moduleTasks[course.id]}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Be Vietnam Pro",
              textAlign: "center",
              marginTop: "20px",
            }}
          >
            No modules available
          </Typography>
        </Box>
      )}
      <Toaster
        open={toasterOpen}
        message={toasterMessage}
        severity={toasterSeverity}
        onClose={handleToasterClose}
      />
      <AdhocScheduleMeetingModal
        open={openAdhocMeeting}
        onClose={() => setOpenAdhocMeeting(false)}
        courseId={id}
      />
    </>
  );
};

export default ModulesCard;